import { Component, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { ConcernDataModel, ConcernDetails, GroupedCatalog, GroupedCategory, ServiceCatalogInfo, ServiceCatalogPayload, ServiceCatalogRedirection, ServiceCatalogResponse, ServiceProviderInfo } from 'src/app/models/request-service.model';
import { CoreService } from 'src/app/services/core/core.service';
import { RequestServiceProvider } from 'src/app/services/providers/request-service-provider/request-service-provider';
import { VehicleProvider } from 'src/app/services/providers/vehicle-provider/vehicle-provider';
import { RequestBookingService } from 'src/app/services/request-booking/request-booking.service';

@Component({
  selector: 'app-request-service',
  templateUrl: './request-service.page.html',
  styleUrls: ['./request-service.page.scss'],
  standalone: false
})
export class RequestServicePage {

  isMobileApp = signal<boolean>(false);
  concernText = signal<string>('');
  concernCount = signal<number>(0);
  icnNo = signal<number>(0);
  currentComponent = signal<string>('');
  title = signal<string>('');
  description = signal<string>('');
  concernData = signal<ConcernDataModel | null>(null);
  isCardHeaderClicked = signal<boolean>(false);
  lastVisitedId = signal<number | null>(null);
  groupedCatalogData = signal<GroupedCategory[]>([]);
  providerData = signal<ServiceCatalogRedirection | null>(null);
  idList = signal<number[]>([]);
  serviceProviderInfo = signal<ServiceProviderInfo | null>(null);
  isVendorInfoAvailable = signal<boolean>(false);

  constructor(
    private readonly alertController: AlertController,
    private readonly coreService: CoreService,
    private readonly vehicleProvider: VehicleProvider,
    private readonly requestServiceProvider: RequestServiceProvider,
    private readonly router: Router,
    private readonly requestBookingService: RequestBookingService
  ) { }

  /**
   * Ionic lifecycle hook that triggers page initialization whenever the view is about to enter.
   */
  ionViewWillEnter() {
    this.initPage();
  }

  /**
   * Initializes page state including platform context and selected vehicle details.
   */
  async initPage(): Promise<void> {
    this.isMobileApp.set(this.coreService.isMobileApp);

    const icnNo = await this.vehicleProvider.readSelectedVehicle()?.icN_NO;
    this.icnNo.set(icnNo ?? 0);

    this.handleConcernData();
  }

  /**
   * Reads persisted concern data and restores navigation context before fetching catalog.
   */
  handleConcernData() {
    const concernData = this.requestServiceProvider.readConcernData();
    this.concernData.set(concernData);

    if (concernData?.directToServiceProvider && concernData?.selectedProviderKey) {
      this.showServiceProviderDirectly(concernData.selectedProviderKey);
      return;
    }

    const navigationStart = concernData?.navigateFrom ?? 'Home';
    let catalogId = null;

    // Restore navigation history when returning from Concern flow
    if (navigationStart == 'Concern') {
      this.idList.set(concernData?.navigateHistory || []);
      catalogId = concernData ? concernData.parentId : null;
    }

    this.getServiceCatalog(catalogId);
    this.idList.set(catalogId != null ? this.idList() : []);
  }

  /**
   * Bypasses the catalog flow and renders the service provider page directly for the given
   * provider key. Used when every remaining basket item belongs to the same service provider.
   */
  showServiceProviderDirectly(providerKey: string) {
    this.idList.set([]);
    this.currentComponent.set('SERVICE_PROVIDER');

    let concernData = this.requestServiceProvider.readConcernData();
    const matchingConcern = concernData?.concernList?.find(item => item.provideR_KEY === providerKey);
    // const serviceTitle = matchingConcern?.sC_SERVICE_TITLE ?? '';
    const serviceTitle = concernData?.concernList
      ?.map(item => item.sC_SERVICE_TITLE)
      .filter(Boolean)
      .join(', ') ?? '';

    this.providerData.set({
      provideR_TITLE: null,
      sC_TYPE_KEY: 'SERVICE_PROVIDER',
      sC_SERVICE_TITLE: serviceTitle,
      sC_SERVICE_DESCRIPTION: null,
      provideR_DESCRIPTION: null,
      provideR_LOGO: null,
      restarT_POINT_TYPE_KEY: null,
      provideR_KEY: providerKey,
      vendoR_SERVICE_ID: 0,
      vehiclE_TYPE_KEY: null,
      sC_RESTART_POINT: false,
      sC_SMR_COMMENT_MANDATORY: false,
      sC_SMR_COMMENT_PROMPT: null,
      sC_SMR_COMPLAINT_CODE: null,
      sC_SMR_FORCE_REVIEW: false,
      sC_SMR_FORCE_WARRANTY: false
    });

    // Clear the one-time redirection flags so normal navigation resumes afterwards
    this.requestServiceProvider.setConcernData('directToServiceProvider', false);
    this.requestServiceProvider.setConcernData('selectedProviderKey', null);
    this.requestServiceProvider.resetConcernData();
    concernData = this.requestServiceProvider.readConcernData();

    // Keep the page's cached concern data in sync so any later recompute (e.g. on
    // back navigation into MENU) doesn't resurrect the draft booking card with stale counts
    this.concernData.set(concernData);
    this.concernCount.set(concernData?.concernList?.length ?? 0);
  }

  /**
   * Fetches service catalog based on the provided catalog id and current navigation context.
   */
  getServiceCatalog(id: number | null) {
    const requestBooking = this.getRequestObj(id);
    this.requestBookingService.getServiceCatalog(requestBooking).subscribe((data) => {
      this.handleserviceCatalogResponse(data, id);
    });
  }

  /**
   * Handles service catalog API response, validates restart point compatibility and routes flow.
   */
  handleserviceCatalogResponse(data: ServiceCatalogResponse, id: number | null) {
    let restartPointType = null;

    if (data && data.serviceCatalogRedirection && data.serviceCatalogRedirection.restarT_POINT_TYPE_KEY != null) {
      restartPointType = data.serviceCatalogRedirection.restarT_POINT_TYPE_KEY;
      const concernData = this.requestServiceProvider.readConcernData();
      let restartVal = concernData?.restartPoint;
      const restartPointId = concernData?.restartPointId;
      // Prevent mixing incompatible booking restart flows
      if (restartVal != null && restartVal != restartPointType) {
        this.presentAlert(restartVal, restartPointType, id, data);
        return;
      }
      if (restartPointId == null) {
        this.requestServiceProvider.setConcernData('restartPointId', id);
      }
      this.setRestartPoint(restartPointType);
    }

    this.manageRequest(id, data, restartPointType);
  }

  /**
   * Displays a confirmation alert when user attempts to mix incompatible concern types in a booking.
   */
  async presentAlert(restartVal: string, restartPointType: string, id: number | null, data: ServiceCatalogResponse) {
    let currentType = restartVal == 'ANCILLARY' ? 'an' : 'a';
    let firstType = restartPointType == 'ANCILLARY' ? 'an' : 'a';

    const alert = await this.alertController.create({
      cssClass: 'custom-alert',
      mode: 'md',
      header: 'There is a problem',
      message: 'You can not add ' + firstType + ' ' + restartPointType.toLocaleLowerCase() + ' concerns to ' + currentType + ' ' + restartVal.toLocaleLowerCase() + ' booking. Your previously selected concern(s) will be removed if you continue.',
      buttons: [{
        text: 'Cancel',
        role: 'cancel',
        cssClass: 'bg-light-cancel',
        handler: (blah) => {
        },
      },
      {
        text: 'Continue',
        cssClass: 'bg-continue',
        handler: () => {
          // Reset concern state when switching to a different restart flow
          this.requestServiceProvider.resetConcernData();
          this.concernCount.set(0);
          this.requestServiceProvider.setConcernData('restartPointId', id);
          this.requestServiceProvider.setConcernData('restartPoint', restartPointType);
          this.concernData.set(this.requestServiceProvider.readConcernData());
          this.manageRequest(id, data, restartPointType);
        },
      },],
    });

    await alert.present();
  }

  /**
   * Persists the restart point type in concern data for consistent booking flow.
   */
  setRestartPoint(restartPointType: string) {
    this.requestServiceProvider.setConcernData('restartPoint', restartPointType);
  }

  /**
   * Central handler that controls UI state, navigation flow and component rendering based on catalog type.
   */
  manageRequest(id: number | null, data: ServiceCatalogResponse, restartPoint: string | null) {
    const catalogRedirectionData = data.serviceCatalogRedirection;

    this.title.set(catalogRedirectionData.sC_SERVICE_TITLE);
    this.description.set(catalogRedirectionData.sC_SERVICE_DESCRIPTION ?? '');
    this.currentComponent.set(catalogRedirectionData.sC_TYPE_KEY);

    const serviceCatalogInfo = data.serviceCatalogInfo;

    this.checkForCallHolman(id, data.serviceCatalogInfo, restartPoint);

    if (this.currentComponent() == "MENU") {
      this.handleMenuComponent(id, serviceCatalogInfo);
    }
    else if (this.currentComponent() == "SERVICE_PROVIDER") {
      this.requestServiceProvider.setConcernData('navigateHistory', this.idList());
      this.providerData.set(data.serviceCatalogRedirection);
    }
    else if (data.serviceCatalogRedirection.sC_TYPE_KEY == "SMR_BOOKING") {
      this.requestServiceProvider.setConcernData('navigateHistory', this.idList());
      this.handleSMRBooking(data, id);
    }
    else if (data.serviceCatalogRedirection.sC_TYPE_KEY == "VENDOR_SERVICE_LIST") {
      this.isVendorInfoAvailable.set(false);
      this.requestServiceProvider.setConcernData('navigateFrom', 'Home');
      this.providerData.set(data.serviceCatalogRedirection);
    }

    this.isCardHeaderClicked.set(false);
  }

  /**
   * Processes SMR booking flow by constructing and updating concern list with duplicate and MOT handling.
   */
  handleSMRBooking(data: ServiceCatalogResponse, catalogId: number | null) {
    const concernData = this.requestServiceProvider.readConcernData();
    const redirection = data.serviceCatalogRedirection;

    const currentName = this.getConcernName(concernData, redirection.sC_SERVICE_TITLE);
    this.requestServiceProvider.setConcernData('duplicateError', '');

    let concernObj: ConcernDetails = {
      commentText: '',
      concernName: currentName,
      catalogId: catalogId ?? this.lastVisitedId() ?? 0,
      sC_TYPE_KEY: redirection.sC_TYPE_KEY,
      sC_SERVICE_TITLE: redirection.sC_SERVICE_TITLE,
      sC_SMR_COMMENT_MANDATORY: redirection.sC_SMR_COMMENT_MANDATORY,
      sC_SMR_COMMENT_PROMPT: redirection.sC_SMR_COMMENT_PROMPT ?? '',
      atA_CODE: '',
      causE_CODE: '',
      linE_TYPE: '',
      repaiR_CODE: '',
      comP_CODE: '',
      provideR_KEY: redirection.provideR_KEY ?? null
    };

    // Merge ATA metadata if available from API response
    if (data.serviceCatalogATAInfo?.length) {
      concernObj = { ...concernObj, ...data.serviceCatalogATAInfo[0] };
    }

    const isOverdue = redirection.sC_SERVICE_TITLE === 'Overdue';
    this.requestServiceProvider.setConcernData('isOverdue', isOverdue);

    // First concern creation flow
    if (!concernData) {
      this.updateConcernList([concernObj]);
      this.redirectBookingConcern();
      return;
    }

    const updatedList = this.buildUpdatedConcernList(
      concernData.concernList ?? [],
      concernObj
    );

    this.updateConcernList(updatedList);
    this.redirectBookingConcern();
  }

  /**
   * Handles MENU component logic including navigation state and grouping catalog data by category.
   */
  handleMenuComponent(id: number | null, serviceCatalogInfo: ServiceCatalogInfo[]) {
    this.manageNavigation(id);
    this.concernCount.set(this.concernData()?.concernList.length ?? 0);
    this.concernText.set(this.concernCount() + `${this.concernCount() == 1 ? ' Concern' : ' Concerns'} `);
    this.requestServiceProvider.setConcernData('parentName', this.title());
    this.requestServiceProvider.setConcernData('parentId', id);
    this.requestServiceProvider.setConcernData('navigateFrom', 'Home');

    const groupedCatalog = serviceCatalogInfo.reduce((groupedData: GroupedCatalog, item) => {
      const categoryTitle = item.categorY_TITLE;
      if (!groupedData[categoryTitle]) {
        groupedData[categoryTitle] = {
          categorY_TITLE: item.categorY_TITLE,
          categorY_DESCRIPTION: item.categorY_DESCRIPTION,
          items: []
        };
      }

      groupedData[categoryTitle].items.push(item);
      return groupedData;
    }, {});

    const groupedCatalogData = Object.values(groupedCatalog)
    this.groupedCatalogData.set(groupedCatalogData);
  }

  /**
   * Checks for special booking restrictions (Call Holman) or auto-redirection scenarios.
   */
  checkForCallHolman(id: number | null, serviceCatalogInfo: ServiceCatalogInfo[], restartPoint: string | null) {
    if (id == null || !serviceCatalogInfo) {
      return;
    }

    const matchingItem = serviceCatalogInfo.find(item => item.folloW_ON_CANNOT_BOOK == 1);
    const filteredMenu = serviceCatalogInfo.filter(sc => sc.folloW_ON_CANNOT_BOOK == 0);

    if (matchingItem) {
      this.requestServiceProvider.setConcernData('callHolmanDetails', matchingItem);
      this.getBookings(matchingItem, filteredMenu, restartPoint, this.isCardHeaderClicked());
    } else if (filteredMenu && filteredMenu.length == 1 && this.isCardHeaderClicked()) {
      this.idList.update(() => []);
      this.handleSingleRedirection(null, null);
    }
    else if (filteredMenu && filteredMenu.length == 1) {
      this.idList.update(() => []);
      this.handleSingleRedirection(filteredMenu[0].servicE_CATALOG_ID, restartPoint);
    }
  }

  /**
   * Fetches existing bookings to determine whether to redirect to restricted catalog or fallback menu.
   */
  getBookings(matchingItem: ServiceCatalogInfo, filteredMenu: ServiceCatalogInfo[], restartPoint: string | null, isCardHeaderClicked: boolean) {
    this.requestBookingService.getBookings(false, 'VehicleIcnNo eq ' + this.icnNo()).subscribe((resp: any) => {
      if (!resp) {
        return;
      }

      if (resp.openBookingCount >= 2) {
        this.requestServiceProvider.setConcernData('navigateFrom', 'Home');
        this.getServiceCatalog(matchingItem.servicE_CATALOG_ID);
      } else if (filteredMenu?.length == 1 && isCardHeaderClicked) {
        this.handleSingleRedirection(null, null);
      }
      else if (filteredMenu?.length == 1) {
        this.handleSingleRedirection(filteredMenu[0].servicE_CATALOG_ID, restartPoint);
      }
    });
  }

  /**
   * Handles FastFit selection and switches view to vendor service list with selected provider data.
   */
  handleFastFit(data: ServiceProviderInfo) {
    this.isVendorInfoAvailable.set(true);
    this.currentComponent.set("VENDOR_SERVICE_LIST");
    this.serviceProviderInfo.set(data);
    this.idList.set([]);
  }

  /**
   * Marks header interaction and stores last visited id for back-navigation logic.
   */
  onHeaderClick() {
    this.isCardHeaderClicked.set(true);
    if (this.idList().length == 1) {
      this.lastVisitedId.set(this.idList()[0]);
    }
  }

  /**
   * Handles catalog header click and navigates to the previous catalog level.
   */
  catalogDataHeaderClicked() {
    const idList = this.idList();
    this.onHeaderClick();
    this.getServiceCatalog(idList[idList.length - 2]);
  }

  /**
   * Handles back navigation action triggered from child components.
   */
  btnClicked() {
    this.onHeaderClick();
    this.getServiceCatalog(this.idList()[this.idList().length - 2]);
  }

  /**
   * Redirects user to the booking concern page.
   */
  redirectBookingConcern(): void {
    this.router.navigateByUrl('/booking-concern');
  }

  /**
   * Manages hierarchical navigation stack for catalog traversal and back navigation behavior.
   */
  manageNavigation(id: number | null) {

    if (!id) {
      return;
    }

    let navHis = this.requestServiceProvider.readConcernData()?.navigateFrom || 'Home';
    if (navHis !== 'Home') {
      return;
    }

    if (id == null) {
      this.idList.set([]);
      return;
    }

    if (!this.idList().length) {
      this.idList.update(x => [...x, id]);
      return;
    }

    let tempIndex = this.idList().indexOf(id);
    if (tempIndex == -1) {
      this.idList.update(x => [...x, id]);
    } else if (this.isCardHeaderClicked()) {
      let len = this.idList().length;
      this.lastVisitedId.set(len ? this.idList()[len - 1] : id);
      this.idList.update(list => {
        const newList = [...list];
        newList.splice(newList.length - 1, 1);
        return newList;
      });
    }
  }

  /**
   * Handles single catalog redirection and persists restart point context.
   */
  handleSingleRedirection(catalogId: number | null, restartPointType: string | null) {
    this.getServiceCatalog(catalogId);
    this.requestServiceProvider.setConcernData('restartPoint', restartPointType);
  }

  /**
   * Builds the service catalog request payload based on navigation origin and restart flow.
   */
  getRequestObj(id: number | null): ServiceCatalogPayload {
    const concernData = this.requestServiceProvider.readConcernData();
    const navigateFrom = concernData?.navigateFrom ?? 'Home';
    const isHomeNavigation = navigateFrom == 'Home';

    return {
      catalogId:
        isHomeNavigation
          ? id?.toString() ?? null
          : concernData?.parentId?.toString() ?? null,
      icnNo: this.icnNo(),
      restartPointTypeKey:
        isHomeNavigation
          ? null
          : concernData?.restartPoint ?? null,
      basketHasItems: concernData?.concernList?.length > 0 ? true : false
    };
  }

  /**
   * Generates concern name based on restart point context and parent hierarchy.
   */
  private getConcernName(
    concernData: any,
    serviceTitle: string
  ): string {
    if (concernData?.restartPoint === 'EQUIPMENT') {
      return serviceTitle;
    }
    return `${concernData?.parentName ?? ''} - ${serviceTitle}`;
  }

  /**
   * Builds updated concern list with duplicate prevention and MOT replacement business rules.
   */
  private buildUpdatedConcernList(
    existingList: ConcernDetails[],
    newObj: ConcernDetails
  ): ConcernDetails[] {
    const motTitles = ['Due within next 8 weeks', 'Overdue', 'Not due'];

    const alreadyExists = existingList.some(
      item => item.concernName === newObj.concernName
    );

    if (alreadyExists) {
      this.requestServiceProvider.setConcernData('duplicateError',
        `You have already added ${newObj.concernName} to your booking.`);
      return existingList;
    }

    const isMotService = motTitles.includes(newObj.sC_SERVICE_TITLE);

    if (!isMotService) {
      return [...existingList, newObj];
    }

    const motIndex = existingList.findIndex(item =>
      motTitles.includes(item.sC_SERVICE_TITLE)
    );

    if (motIndex > -1) {
      const updated = [...existingList];
      updated[motIndex] = newObj;
      return updated;
    }

    return [...existingList, newObj];
  }

  /**
   * Updates concern list in provider state.
   */
  private updateConcernList(list: ConcernDetails[]) {
    this.requestServiceProvider.setConcernData('concernList', list);
  }

}